import { useCallback, useEffect, useMemo, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Languages, CheckCircle2,
  AlertTriangle, RotateCcw, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  fetchNonEnglishProducts,
  translateProductIngredients,
  rollbackProductTranslation,
} from "../lib/api";
import { formatApiError } from "../lib/apiError";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = "medskin_admin_token";

const LANG_LABEL = {
  pt: "Portuguese",
  es: "Spanish",
  fr: "French",
  de: "German",
  nl: "Dutch",
  "??": "Mixed/Unknown",
};
const LANG_TONE = {
  pt: "bg-amber-50 border-amber-200 text-amber-800",
  es: "bg-rose-50 border-rose-200 text-rose-800",
  fr: "bg-indigo-50 border-indigo-200 text-indigo-800",
  de: "bg-slate-50 border-slate-200 text-slate-800",
  nl: "bg-teal-50 border-teal-200 text-teal-800",
  "??": "bg-stone-50 border-stone-200 text-stone-800",
};

function StatTile({ label, value, tone, testid }) {
  const TONES = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${TONES[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function ProductRow({ row, onTranslated, onRolledBack }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const lang = row.detected_language || "??";

  const translate = async () => {
    setBusy(true);
    try {
      const result = await translateProductIngredients(row.id);
      setPreview(result.after);
      toast.success(`Translated: ${row.brand || ""} ${row.full_name || row.name || ""}`);
      onTranslated?.(row.id, result);
    } catch (e) {
      toast.error(formatApiError(e, "Translation failed"));
    } finally {
      setBusy(false);
    }
  };

  const rollback = async () => {
    if (!window.confirm("Restore the original (non-English) ingredient list for this product?")) return;
    setBusy(true);
    try {
      await rollbackProductTranslation(row.id);
      toast.success(`Rolled back: ${row.brand || ""} ${row.full_name || row.name || ""}`);
      onRolledBack?.(row.id);
    } catch (e) {
      toast.error(formatApiError(e, "Rollback failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-surface-line/60 hover:bg-cream-50/50">
      <td className="px-3 py-2.5 align-top">
        <div className="text-[13px] font-semibold text-ink leading-tight">
          {row.brand || "—"}
        </div>
        <div className="text-[12px] text-ink-mute leading-tight mt-0.5">
          {row.full_name || row.name || row.id}
        </div>
        <div className="text-[10px] text-ink-mute/70 mt-0.5 font-mono">{row.id}</div>
      </td>
      <td className="px-3 py-2.5 align-top">
        <span
          className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${LANG_TONE[lang]}`}
          data-testid={`dq-lang-${row.id}`}
        >
          {LANG_LABEL[lang] || lang}
        </span>
      </td>
      <td className="px-3 py-2.5 align-top max-w-md">
        <div className="text-[11px] text-ink-mute font-mono whitespace-pre-wrap leading-tight">
          {row.ingredients_text_preview}
          {row.ingredients_text_full_len > 200 && "…"}
        </div>
        {preview && (
          <div className="mt-2 p-2 rounded-md bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">
              ✓ Translated (English INCI)
            </div>
            <div className="text-[11px] text-emerald-900 font-mono whitespace-pre-wrap leading-tight" data-testid={`dq-translated-${row.id}`}>
              {preview.slice(0, 220)}{preview.length > 220 && "…"}
            </div>
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 align-top whitespace-nowrap text-right">
        {row.already_translated ? (
          <Button
            size="sm" variant="outline"
            onClick={rollback} disabled={busy}
            data-testid={`dq-rollback-${row.id}`}
            className="text-[11px]"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            <span className="ml-1">Rollback</span>
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={translate} disabled={busy}
            data-testid={`dq-translate-${row.id}`}
            className="text-[11px]"
          >
            {busy
              ? <><Loader2 className="w-3 h-3 animate-spin" /><span className="ml-1">Translating…</span></>
              : <><Sparkles className="w-3 h-3" /><span className="ml-1">Translate</span></>}
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function AdminDataQuality() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, errors: 0 });
  const [bulkLog, setBulkLog] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchNonEnglishProducts();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load non-English products"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const counts = useMemo(() => {
    const byLang = {};
    let translated = 0;
    for (const r of rows) {
      const k = r.detected_language || "??";
      byLang[k] = (byLang[k] || 0) + 1;
      if (r.already_translated) translated += 1;
    }
    return { total: rows.length, byLang, translated, remaining: rows.length - translated };
  }, [rows]);

  const onTranslated = (id, result) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, already_translated: true, ingredients_text_preview: result.after.slice(0, 200), ingredients_source: "claude_translation" }
          : r,
      ),
    );
  };

  const onRolledBack = () => { reload(); };

  // Bulk translate via streaming NDJSON.
  const translateAll = async () => {
    if (!window.confirm(`Translate all ${counts.remaining} remaining products via Claude? This will take ~${Math.ceil(counts.remaining * 4)} seconds.`)) return;
    setBulkRunning(true);
    setBulkLog([]);
    setBulkProgress({ done: 0, total: counts.remaining, errors: 0 });
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    try {
      const resp = await fetch(`${BACKEND}/api/admin/v2/data-quality/translate-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let done = 0, errors = 0, total = counts.remaining;
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const ln of lines) {
          if (!ln.trim()) continue;
          let evt;
          try { evt = JSON.parse(ln); } catch { continue; }
          if (evt.event === "start") {
            total = evt.total;
            setBulkProgress({ done: 0, total, errors: 0 });
          } else if (evt.event === "ok") {
            done += 1;
            setBulkProgress({ done, total, errors });
            setBulkLog((prev) => [{ ...evt, ts: Date.now() }, ...prev].slice(0, 30));
          } else if (evt.event === "error") {
            errors += 1; done += 1;
            setBulkProgress({ done, total, errors });
            setBulkLog((prev) => [{ ...evt, ts: Date.now() }, ...prev].slice(0, 30));
          } else if (evt.event === "done") {
            toast.success(`Bulk translate complete — ${done - errors} ok, ${errors} errors`);
          }
        }
      }
    } catch (e) {
      toast.error(formatApiError(e, "Bulk translate failed"));
    } finally {
      setBulkRunning(false);
      // Refresh the list to reflect new already_translated flags.
      reload();
    }
  };

  const pct = bulkProgress.total ? Math.round((bulkProgress.done / bulkProgress.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-cream-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <AdminHeader />

        <div className="mb-6">
          <h1 className="text-3xl font-extrabold text-ink mb-1">Data Quality</h1>
          <p className="text-[13px] text-ink-mute">
            One-click Claude translation of non-English ingredient lists into standard English INCI.
            Each translation also rebuilds the row's <code className="text-[11px] bg-cream-100 px-1 rounded">key_ingredient_ids</code> so verdicts stay correct.
          </p>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
          <StatTile testid="dq-total" label="Total flagged" value={counts.total} tone="amber" />
          <StatTile testid="dq-translated" label="Translated" value={counts.translated} tone="green" />
          <StatTile testid="dq-remaining" label="Remaining" value={counts.remaining} tone="amber" />
          <StatTile testid="dq-languages" label="Languages" value={Object.keys(counts.byLang).length} tone="slate" />
        </div>

        {/* Bulk control */}
        <div className="rounded-xl border border-surface-line bg-white p-4 mb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[14px] font-bold text-ink flex items-center gap-2">
                <Languages className="w-4 h-4" /> Translate all remaining
              </div>
              <div className="text-[11px] text-ink-mute mt-0.5">
                Runs each row through Claude Sonnet 4.5 with 1 s pacing. Originals are snapshotted to <code className="text-[10px]">products_name_fix_audit</code> for rollback.
              </div>
            </div>
            <Button
              onClick={translateAll}
              disabled={bulkRunning || counts.remaining === 0}
              data-testid="dq-translate-all-btn"
            >
              {bulkRunning
                ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Running…</>
                : <><Sparkles className="w-4 h-4 mr-1.5" />Translate all ({counts.remaining})</>}
            </Button>
          </div>
          {bulkRunning && (
            <div className="mt-3" data-testid="dq-bulk-progress">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="text-ink-mute">
                  {bulkProgress.done} / {bulkProgress.total} processed
                  {bulkProgress.errors > 0 && ` · ${bulkProgress.errors} errors`}
                </span>
                <span className="font-bold text-ink">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-cream-100 overflow-hidden">
                <div
                  className="h-full bg-ink transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
          {bulkLog.length > 0 && (
            <details className="mt-3 text-[11px]" data-testid="dq-bulk-log">
              <summary className="cursor-pointer text-ink-mute">Activity log ({bulkLog.length})</summary>
              <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto font-mono text-[10px]">
                {bulkLog.map((evt, i) => (
                  <li key={i} className={evt.event === "error" ? "text-red-700" : "text-ink-mute"}>
                    {evt.event === "error" ? <AlertTriangle className="w-3 h-3 inline mr-1" /> : <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                    [{evt.index}/{evt.total}] {evt.brand ? `${evt.brand} · ` : ""}{evt.full_name || evt.product_id}
                    {evt.event === "error" && ` — ${evt.detail}`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Table */}
        <div className="rounded-xl border border-surface-line bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-surface-line bg-cream-50/50">
            <div className="text-[11px] uppercase tracking-wider font-bold text-ink-mute">
              Non-English products ({rows.length})
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-ink-mute">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-emerald-700" data-testid="dq-empty-state">
              <CheckCircle2 className="w-5 h-5 inline mr-2" />
              All ingredient lists are in English INCI. Catalog is clean.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="dq-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-ink-mute border-b border-surface-line bg-cream-50/30">
                    <th className="px-3 py-2 text-left font-bold">Product</th>
                    <th className="px-3 py-2 text-left font-bold">Language</th>
                    <th className="px-3 py-2 text-left font-bold">Current ingredients_text</th>
                    <th className="px-3 py-2 text-right font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <ProductRow
                      key={row.id}
                      row={row}
                      onTranslated={onTranslated}
                      onRolledBack={onRolledBack}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
